import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Role } from 'epc-schema'
import { formatPhoneNumber } from 'utils'
import CreatableSelect from 'react-select/creatable'
import { useCompanies } from '../../../queries/useCompanies'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from 'ui'
import { DistrictSelectFn, SchoolSelectFn } from '../FormControls'
import Select from 'react-select'
// Use public endpoints for unauthenticated access in signup form
import { usePublicDistricts } from '../../../queries/usePublicDistricts'
import { usePublicSchools } from '../../../queries/usePublicSchools'
import { SchoolRequestModal } from '../../../components/SchoolRequestModal'

// Validation schema
const signupSchema = yup.object({
  companyName: yup.string().when('userRole', {
    is: 'pro',
    then: (schema) => schema.required('Cannot be blank'),
    otherwise: (schema) => schema.optional(),
  }),
  communityCode: yup.string().optional(),
  districtName: yup.string().when('userRole', {
    is: 'educator',
    then: (schema) => schema.required('Cannot be blank'),
    otherwise: (schema) => schema.optional(),
  }),
  districtId: yup.string().optional(),
  schoolId: yup.string().optional(),
  emailAddress: yup
    .string()
    .email('Must be a valid email address')
    .required('Cannot be blank'),
  firstName: yup.string().required('Cannot be blank'),
  lastName: yup.string().required('Cannot be blank'),
  phone: yup.string().min(10, 'Must be a valid phone number'),
  schoolName: yup.string().when('userRole', {
    is: 'educator',
    then: (schema) => schema.required('Cannot be blank'),
    otherwise: (schema) => schema.optional(),
  }),
  userRole: yup.string().required('You must choose a role'),
  termsAndConditions: yup
    .string()
    .required('You must accept the terms and conditions'),
})
export type Signup = yup.InferType<typeof signupSchema>

const FormComponent: React.FC<{ onSubmit: any }> = ({ onSubmit }) => {
  const form = useForm<Signup>({
    defaultValues: {
      companyName: '',
      communityCode: '',
      districtId: '',
      districtName: '',
      emailAddress: '',
      firstName: '',
      lastName: '',
      phone: '',
      schoolId: '',
      schoolName: '',
      userRole: '',
      termsAndConditions: 'false',
    } as Signup,
    reValidateMode: 'onChange',
    resolver: yupResolver(signupSchema as any) as any,
  })

  const watchUserRole = form.watch('userRole')
  const watchDistrictId = form.watch('districtId')
  const watchCompanyName = form.watch('companyName')
  const hasSchoolDetails =
    watchUserRole === 'EDUCATOR' && Boolean(form.watch('schoolId'))
  const hasProDetails = Boolean(watchCompanyName)
  const selectDistrict: DistrictSelectFn = (district) => {
    form.setValue('districtId', district.id)
    form.setValue('districtName', district.name)
    form.setValue('schoolId', '')
    form.setValue('schoolName', '')
  }

  const selectSchool: SchoolSelectFn = (school) => {
    form.setValue('schoolId', school.id)
    form.setValue('schoolName', school.name)
  }

  const { data: districts } = usePublicDistricts()
  const { data: schools } = usePublicSchools(watchDistrictId)

  const handleSubmitForm = async (values: Signup) => {
    onSubmit(values)
  }

  const [accepted, setAccepted] = useState(false)
  const [showCommunityCodeInput, setShowCommunityCodeInput] = useState(false)
  const [isSchoolRequestModalOpen, setIsSchoolRequestModalOpen] = useState(
    false
  )
  const [userAddedCompany, setUserAddedCompany] = useState<string | null>(null)
  const [companyInputValue, setCompanyInputValue] = useState('')

  // Company name selection
  const { data: companies } = useCompanies()
  const companyOptions = React.useMemo(() => {
    // Only show suggestions if user has typed at least 3 characters
    if (companyInputValue.length < 3) {
      return []
    }

    let options =
      companies?.map((company) => ({
        value: company,
        label: company,
      })) || []

    // Add any user-created company to the list
    if (
      userAddedCompany &&
      companies &&
      !companies.includes(userAddedCompany)
    ) {
      options.push({
        value: userAddedCompany,
        label: userAddedCompany,
      })
    }

    // Always include the currently selected value if it exists and isn't already in options
    if (
      watchCompanyName &&
      !options.some((option) => option.value === watchCompanyName)
    ) {
      options.push({
        value: watchCompanyName,
        label: watchCompanyName,
      })
    }

    // Filter options based on input and sort alphabetically
    return options
      .filter((option) =>
        option.label.toLowerCase().includes(companyInputValue.toLowerCase())
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [companies, userAddedCompany, companyInputValue, watchCompanyName])

  const selectCompany = (company: string) => {
    form.setValue('companyName', company)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitForm)} className="mt-8">
        <div className="flex flex-col sm:grid sm:grid-cols-4 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-row flex mt-4">
          <FormField
            control={form.control}
            name="emailAddress"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="my@emailaddress.com" {...field} />
                </FormControl>
                {watchUserRole === 'EDUCATOR' && (
                  <p className="text-xs text-muted-foreground mt-1 animate-pulse-subtle border-l-2 border-primary pl-2 bg-blue-50/30 py-1 rounded transition-all transform hover:scale-[1.01] duration-300">
                    For better verification and communication, we recommend
                    using your{' '}
                    <span className="font-semibold text-primary">
                      official school email address
                    </span>{' '}
                    if available.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-row flex mt-4 sm:grid sm:grid-cols-4 gap-4">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="w-full col-span-3">
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder=""
                    {...field}
                    onBlur={(e) => {
                      const formattedValue = formatPhoneNumber(e.target.value)
                      field.onChange(formattedValue)
                      field.onBlur()
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="userRole"
          render={({ field }) => (
            <FormItem className="mt-6 flex flex-col w-full">
              <FormLabel className="mb-2">I am an....</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  defaultValue={field.value}
                  className="w-full grid grid-cols-2 gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value={'EDUCATOR'} id="role-educator" />
                    <FormLabel htmlFor="role-educator" className="text-content">
                      Educator
                    </FormLabel>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={'PROFESSIONAL'}
                      id="role-professional"
                    />
                    <FormLabel
                      htmlFor="role-professional"
                      className="text-content"
                    >
                      Industry Professional
                    </FormLabel>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchUserRole === 'EDUCATOR' && (
          <FormField
            control={form.control}
            name="districtId"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>School District</FormLabel>
                <FormControl>
                  <Select
                    isDisabled={form.formState.isSubmitting}
                    placeholder="Choose your district"
                    options={districts?.map((district) => ({
                      value: district,
                      label: district,
                    }))}
                    value={
                      field.value && districts
                        ? districts
                            .filter((district) => district === field.value)
                            .map((district) => ({
                              value: district,
                              label: district,
                            }))[0]
                        : null
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        const selectedDistrict = districts?.find(
                          (d) => d === option.value
                        )
                        if (selectedDistrict) {
                          selectDistrict({
                            id: selectedDistrict,
                            name: selectedDistrict,
                          })
                        }
                      }
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {watchUserRole === 'EDUCATOR' && watchDistrictId && (
          <FormField
            control={form.control}
            name="schoolId"
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>School</FormLabel>
                <FormControl>
                  <Select
                    isDisabled={form.formState.isSubmitting}
                    placeholder="Choose your school"
                    options={schools?.map((school) => ({
                      value: school.id,
                      label: school.name,
                    }))}
                    value={
                      field.value?.length
                        ? schools
                            ?.filter((school) => school.id === field.value)
                            .map((school) => ({
                              value: school.id,
                              label: school.name,
                            }))[0]
                        : null
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        selectSchool({
                          id: option.value,
                          name: option.label,
                        })
                      }
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {watchUserRole === 'EDUCATOR' && (
          <div className="mt-1 text-xs">
            <span
              className="text-actionable underline cursor-pointer"
              onClick={() => setIsSchoolRequestModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setIsSchoolRequestModalOpen(true)
                }
              }}
            >
              Can't find your district/school?
            </span>
          </div>
        )}

        {watchUserRole === 'PROFESSIONAL' && (
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>Company</FormLabel>
                <FormControl>
                  <CreatableSelect
                    isDisabled={form.formState.isSubmitting}
                    placeholder="Name of Your Employer"
                    options={companyOptions}
                    value={
                      companyOptions.find(
                        (option) => option.value === field.value
                      ) ||
                      (field.value
                        ? { value: field.value, label: field.value }
                        : null)
                    }
                    inputValue={companyInputValue}
                    onInputChange={(value) => setCompanyInputValue(value)}
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        selectCompany(option.value)
                      }
                    }}
                    onCreateOption={(inputValue) => {
                      setUserAddedCompany(inputValue)
                      field.onChange(inputValue)
                      selectCompany(inputValue)
                    }}
                    noOptionsMessage={() =>
                      companyInputValue.length < 3
                        ? 'Type at least 3 characters to search'
                        : 'No companies found'
                    }
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {hasSchoolDetails || hasProDetails ? (
          <>
            <div className="mt-6">
              <p className="text-xs mb-2 font-body text-primary">
                Have you received a Community Code for your group, organization,
                or project?{' '}
                {!showCommunityCodeInput ? (
                  <span
                    className="text-actionable underline cursor-pointer"
                    onClick={() => setShowCommunityCodeInput(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setShowCommunityCodeInput(true)
                      }
                    }}
                  >
                    Add it here
                  </span>
                ) : null}
              </p>

              {showCommunityCodeInput && (
                <FormField
                  control={form.control}
                  name="communityCode"
                  render={({ field }) => (
                    <FormItem className="mt-2">
                      <FormControl>
                        <Input placeholder="Enter Community Code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {showCommunityCodeInput ? (
                <span
                  className="text-actionable/75 underline cursor-pointer text-xs font-body"
                  onClick={() => setShowCommunityCodeInput(false)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setShowCommunityCodeInput(false)
                    }
                  }}
                >
                  Nevermind, I'll add this later.
                </span>
              ) : null}
            </div>

            <FormField
              control={form.control}
              name="termsAndConditions"
              render={({ field }) => (
                <FormItem className="mt-6 flex">
                  <FormControl>
                    <Checkbox
                      className="mr-2"
                      checked={accepted}
                      onClick={() => {
                        setAccepted(!accepted)
                      }}
                    />
                  </FormControl>

                  {watchUserRole !== 'PROFESSIONAL' ? (
                    <p className="text-xs font-body">
                      I attest that I am an employed educator in an Arizona K-12
                      school and that I am using this Educator PRO Connect Tool
                      to connect with industry professionals to support my
                      students and myself for education purposes. I can
                      terminate my account at any time which will result in
                      deactivation or disablement of the account and access and
                      the deletion of content collected using the Tool. I
                      understand that the Office of the Maricopa County School
                      Superintendent will approve my account and can delete my
                      account if I do not follow the intended purpose of the
                      Educator PRO Connect Tool.
                    </p>
                  ) : (
                    <p className="text-xs font-body">
                      I attest that I am a volunteer industry professional and
                      that I am using this Educator PRO Connect Tool to support
                      teachers and students in connecting with professionals for
                      education purposes and not for sales of goods or services.
                      I can terminate my account at any time which will result
                      in deactivation or disablement of my account and the
                      deletion of content I collected using the Tool. I
                      understand that the Office of the Maricopa County School
                      Superintendent will approve my account and can delete my
                      account if I do not follow the intended education purposes
                      of the Educator PRO Connect Tool.
                    </p>
                  )}

                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : null}

        <div className="flex-row flex mt-8 w-full">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting || !accepted}
            variant="default"
            className="ml-auto"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>Create My Account</span>
          </Button>
        </div>
      </form>

      <SchoolRequestModal
        isOpen={isSchoolRequestModalOpen}
        onClose={() => setIsSchoolRequestModalOpen(false)}
        defaultEmail={form.watch('emailAddress')}
      />
    </Form>
  )
}

export const InitialSignUp = FormComponent
